import { FC, useEffect, useRef, useState } from 'react';
import { useToggle } from 'react-use';
import { ExpandButton } from 'src/components/common/ExpandableContent/ExpandButton';
import { makeTransition } from 'src/utils/motion/transitions';
import { AnimatePresence, motion } from 'framer-motion';
import { StyleProps } from '@summer/jst-react';
import styled from 'styled-components';
import { transparentize } from 'polished';
import { useUID } from 'react-uid';
import { ScrollToSectionOnClick } from 'src/components/common/ScrollToSectionOnClick';

export interface ExpandableContentProps {
  height?: number;
}

const Content = styled(motion.div).attrs({
  initial: 'collapsed',
  exit: 'collapsed',
  transition: { duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] },
})<{ $visible: boolean }>`
  position: relative;
  overflow: hidden;

  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 40px;
    bottom: 0;
    opacity: ${({ $visible }) => ($visible ? 0 : 1)};
    transition: 0.25s;
    background: ${({ theme }) =>
      `linear-gradient(rgba(245, 246, 248, 0), ${transparentize(
        0.2,
        theme.skeleton.highlight,
      )} 50%)`};
  }
`;

const isOverflowing = (element: HTMLElement | null) => {
  return element ? element.scrollHeight > element.clientHeight : false;
};

export const ExpandableContent: FC<ExpandableContentProps & StyleProps> = ({
  children,
  height = 95,
  ...props
}) => {
  const [isExpanded, toggleIsExpanded] = useToggle(false);

  const ref = useRef<HTMLDivElement>(null);
  const id = useUID();

  const [isContentOverflowing, setIsContentOverflowing] = useState(
    !isExpanded && isOverflowing(ref.current),
  );

  useEffect(() => {
    if (!isExpanded) {
      setIsContentOverflowing(isOverflowing(ref.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  return (
    <motion.div
      id={`expandable-${id}`}
      {...makeTransition({ fade: true })}
      {...props}
    >
      <AnimatePresence initial={false}>
        <Content
          ref={ref}
          $visible={isExpanded || !isContentOverflowing}
          animate={isExpanded ? 'open' : 'collapsed'}
          variants={{
            open: { height: 'auto' },
            collapsed: { height: `calc(var(--base-unit) * ${height})` },
          }}
        >
          {children}
        </Content>
      </AnimatePresence>
      {isContentOverflowing && (
        <ScrollToSectionOnClick id={`expandable-${id}`} offset={60}>
          <ExpandButton active={isExpanded} onClick={toggleIsExpanded} />
        </ScrollToSectionOnClick>
      )}
    </motion.div>
  );
};
